import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { apiClient, getUserId, isAuthenticated } from '../../utils/api';

const initialState = {
    items: [],
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
};

export const fetchSessions = createAsyncThunk(
    'sessions/fetch',
    async (_, { rejectWithValue }) => {
        if (!isAuthenticated()) return [];
        const userId = getUserId();
        if (!userId) return [];
        try {
            const data = await apiClient.fetchSessions(userId);
            return data || [];
        } catch (err) {
            return rejectWithValue(err?.message || 'fetch failed');
        }
    },
    {
        condition: (_, { getState }) => {
            const { status } = getState().sessions;
            if (status === 'loading' || status === 'succeeded') return false;
            return true;
        },
    },
);

const sessionsSlice = createSlice({
    name: 'sessions',
    initialState,
    reducers: {
        sessionsReplaced(state, action) {
            state.items = action.payload;
        },
        sessionAdded(state, action) {
            const session = action.payload;
            const idx = state.items.findIndex((s) => s.session_id === session.session_id);
            if (idx >= 0) {
                state.items.splice(idx, 1);
            }
            state.items.unshift(session);
        },
        sessionUpdated(state, action) {
            const { id, patch } = action.payload;
            const idx = state.items.findIndex((s) => s.session_id === id);
            if (idx >= 0) {
                state.items[idx] = { ...state.items[idx], ...patch };
            }
        },
        sessionRemoved(state, action) {
            state.items = state.items.filter((s) => s.session_id !== action.payload);
        },
        sessionsReset() {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSessions.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchSessions.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.items = action.payload;
            })
            .addCase(fetchSessions.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload || 'unknown';
            });
    },
});

export const {
    sessionsReplaced,
    sessionAdded,
    sessionUpdated,
    sessionRemoved,
    sessionsReset,
} = sessionsSlice.actions;

export default sessionsSlice.reducer;

// Selectors
const selectRoot = (state) => state.sessions;
export const selectSessions = createSelector(selectRoot, (root) => root.items);
export const selectSessionsStatus = createSelector(selectRoot, (root) => root.status);
export const selectIsSessionsInitializing = createSelector(
    selectRoot,
    (root) => root.status === 'idle' || root.status === 'loading',
);
export const selectSessionById = (id) =>
    createSelector(selectSessions, (items) => items.find((s) => s.session_id === id) || null);
